import { useState } from 'react';
import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router';
import { PlusCircle, Target } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/common/PageHeader';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorState } from '@/components/common/Feedback';
import { LoadingRows } from '@/components/common/Feedback';
import { Mono } from '@/components/common/display';
import { ReverseLookupCard } from '@/components/cgnat/ReverseLookupCard';
import { CreatePoolDialog } from '@/components/cgnat/CreatePoolDialog';
import { usePools } from '@/hooks/use-cgnat';
import { formatPortRange } from '@/lib/format';
import type { CgnatPool } from '@/lib/types';

export interface CgnatSearch {
  public?: string;
  port?: number;
  private?: string;
}

function toNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

export const Route = createFileRoute('/_auth/cgnat')({
  validateSearch: (search: Record<string, unknown>): CgnatSearch => ({
    public: typeof search.public === 'string' ? search.public : undefined,
    port: toNumber(search.port),
    private: typeof search.private === 'string' ? search.private : undefined,
  }),
  component: CgnatRoute,
});

const MODE_LABEL: Record<string, string> = {
  deterministic: 'Determinístico',
  dynamic: 'Dinámico',
  'endpoint-independent': 'EI',
};

const MODE_VARIANT: Record<
  string,
  'default' | 'secondary' | 'info' | 'warning' | 'success' | 'outline'
> = {
  deterministic: 'success',
  dynamic: 'info',
  'endpoint-independent': 'warning',
};

interface PoolCardProps {
  pool: CgnatPool;
  onClick: () => void;
}

function PoolCard({ pool, onClick }: PoolCardProps) {
  const modeLabel = MODE_LABEL[pool.mode] ?? pool.mode;
  const modeVariant = MODE_VARIANT[pool.mode] ?? 'secondary';

  return (
    <Card
      className="cursor-pointer transition-colors hover:border-primary/50 hover:bg-accent/30"
      onClick={onClick}
    >
      <CardContent className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-foreground">{pool.name}</p>
            <div className="mt-0.5 flex items-center gap-1 text-[10px] text-muted-foreground">
              <Mono>{pool.private_prefix}</Mono>
              <span>→</span>
              <Mono>{pool.public_prefix}</Mono>
            </div>
          </div>
          <Badge variant={modeVariant} className="shrink-0 text-[10px]">
            {modeLabel}
          </Badge>
        </div>
        <div className="mt-2 flex items-center gap-3 text-[10px] text-muted-foreground">
          <span>
            <span className="font-mono text-foreground">{pool.subscribers_per_public}</span>{' '}
            subs/IP
          </span>
          <span>
            Puertos{' '}
            <Mono className="text-foreground">
              {formatPortRange(pool.port_block_start, pool.port_block_end)}
            </Mono>
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function CgnatRoute() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const poolsQuery = usePools();

  const [createOpen, setCreateOpen] = useState(false);

  // Build initial for ReverseLookupCard from search params (command palette handoff)
  const hasLookupParams = Boolean(
    (search.public && search.port) || search.private,
  );

  const pools = poolsQuery.data ?? [];

  return (
    <div className="flex min-h-0 h-full flex-col">
      <PageHeader
        title="CGNAT"
        icon={Target}
        description="Pools determinísticos, mappings y reverse-lookup"
        actions={
          <Button
            size="sm"
            className="gap-1.5"
            onClick={() => setCreateOpen(true)}
          >
            <PlusCircle className="size-3.5" />
            Nuevo pool
          </Button>
        }
      />

      {/* Pool list strip */}
      <div className="border-b border-border p-4">
        {poolsQuery.isLoading ? (
          <LoadingRows rows={3} />
        ) : poolsQuery.error ? (
          <ErrorState error={poolsQuery.error} resource="Pools CGNAT" />
        ) : pools.length === 0 ? (
          <EmptyState
            icon={Target}
            title="Sin pools CGNAT"
            description="Crea tu primer pool para comenzar a gestionar mappings."
            action={
              <Button size="sm" className="gap-1.5" onClick={() => setCreateOpen(true)}>
                <PlusCircle className="size-3.5" />
                Nuevo pool
              </Button>
            }
          />
        ) : (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {pools.map((pool) => (
              <PoolCard
                key={pool.id}
                pool={pool}
                onClick={() =>
                  void navigate({
                    to: '/cgnat/$poolId',
                    params: { poolId: pool.id },
                  })
                }
              />
            ))}
          </div>
        )}

        {/* Inline reverse-lookup when search params arrive from command palette */}
        {hasLookupParams && (
          <div className="mt-4 max-w-sm">
            <ReverseLookupCard
              initial={{
                public: search.public,
                port: search.port,
                private: search.private,
              }}
            />
          </div>
        )}
      </div>

      {/* Pool detail renders below via Outlet */}
      <div className="min-h-0 flex-1">
        <Outlet />
      </div>

      <CreatePoolDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}
